import { useMemo } from 'react';

type TStringPath = string[];

// 这里不直接依赖 tea-component 的类型定义，避免构建时类型解析问题
interface ICascaderMenuOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  children?: ICascaderMenuOption[];
}

type TSecondLevelMap = Record<string, string | undefined>;

const buildSecondLevelMap = (paths: TStringPath[]): TSecondLevelMap => {
  const result: TSecondLevelMap = {};

  paths.forEach((path) => {
    if (!Array.isArray(path) || path.length < 2) return;
    const [level1, level2] = path;
    if (!level1 || !level2) return;
    // 仅在当前第一级还没有记录时写入，确保优先保留“第一次出现”的第二级
    if (!result[level1]) {
      result[level1] = level2;
    }
  });

  return result;
};

const computeDisabledOptions = (
  data: ICascaderMenuOption[],
  value: TStringPath[],
): ICascaderMenuOption[] => {
  const secondLevelMap = buildSecondLevelMap(value);

  return data.map((level1) => {
    const level1Value = level1.value;
    const chosenLevel2 = secondLevelMap[level1Value];

    if (!level1.children || !chosenLevel2) {
      return {
        ...level1,
        children: level1.children?.map((child) => ({
          ...child,
          children: child.children?.map((grandChild) => ({ ...grandChild })),
        })),
      };
    }

    return {
      ...level1,
      children: level1.children.map((level2) => ({
        ...level2,
        disabled: level2.value !== chosenLevel2 || level2.disabled,
        children: level2.children?.map((level3) => ({ ...level3 })),
      })),
    };
  });
};

/**
 * 基于当前选中值，为 `data` 补齐二级 `disabled` 状态（同一个一级下二级单选）。
 * 说明：该 Hook 只负责数据归一化，不耦合组件内部状态管理。
 */
const useAbTestCascaderDisabledOptions = (
  data: ICascaderMenuOption[],
  value: TStringPath[],
) => {
  return useMemo(() => computeDisabledOptions(data, value), [data, value]);
};

export default useAbTestCascaderDisabledOptions;

